import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Badge, Select, Loading, EmptyState } from '../components/ui';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function MonthlySummary() {
  const [factories, setFactories] = useState([]);
  const [factoryId, setFactoryId] = useState('');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-indexed

  const [machineData, setMachineData] = useState(null);
  const [trainingData, setTrainingData] = useState(null);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [loadingTraining, setLoadingTraining] = useState(false);
  const [tab, setTab] = useState('machines'); // 'machines' | 'training'

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Monthly summary'; }, []);

  useEffect(() => {
    api.get('/factories').then(({ data }) => {
      setFactories(data);
      if (data.length && !factoryId) setFactoryId(String(data[0].id));
    }).catch(console.error);
  }, []);

  const fetchMachines = useCallback(async () => {
    if (!factoryId) return;
    setLoadingMachines(true);
    try {
      const { data } = await api.get('/monthly-summary/machines', { params: { factory_id: factoryId, year, month } });
      setMachineData(data);
    } catch (e) { console.error(e); setMachineData(null); }
    setLoadingMachines(false);
  }, [factoryId, year, month]);

  const fetchTraining = useCallback(async () => {
    if (!factoryId) return;
    setLoadingTraining(true);
    try {
      const { data } = await api.get('/monthly-summary/training', { params: { factory_id: factoryId } });
      setTrainingData(data);
    } catch (e) { console.error(e); setTrainingData(null); }
    setLoadingTraining(false);
  }, [factoryId]);

  useEffect(() => { fetchMachines(); }, [fetchMachines]);
  useEffect(() => { fetchTraining(); }, [fetchTraining]);

  const factoryOptions = factories.map(f => ({ value: f.id, label: f.name }));
  const yearOptions = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => ({ value: y, label: String(y) }));
  const monthOptions = MONTH_NAMES.map((name, i) => ({ value: i + 1, label: name }));

  // Collect the union of machine types that appear across the whole month, for consistent columns
  const allMachineTypes = machineData
    ? [...new Set(machineData.days.flatMap(d => d.machines.map(m => m.machine_type)))].sort()
    : [];

  const deficientDayCount = machineData ? machineData.days.filter(d => d.has_deficiency).length : 0;

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Monthly summary</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Machine requirement and training need across active + pending deployments</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Select value={factoryId} onChange={setFactoryId} options={factoryOptions} placeholder="Factory" style={{ minWidth: 140 }} />
          <Select value={month} onChange={v => setMonth(Number(v))} options={monthOptions} style={{ minWidth: 130 }} />
          <Select value={year} onChange={v => setYear(Number(v))} options={yearOptions} style={{ minWidth: 90 }} />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <Btn variant={tab === 'machines' ? 'primary' : 'default'} size="sm" onClick={() => setTab('machines')}>
          Machine requirement {machineData && deficientDayCount > 0 && <Badge type="red" size="xs">{deficientDayCount} short</Badge>}
        </Btn>
        <Btn variant={tab === 'training' ? 'primary' : 'default'} size="sm" onClick={() => setTab('training')}>
          Training need {trainingData && trainingData.gaps.length > 0 && <Badge type="amber" size="xs">{trainingData.gaps.length} gaps</Badge>}
        </Btn>
      </div>

      {/* Machine requirement tab */}
      {tab === 'machines' && (
        <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
          {loadingMachines ? <Loading /> : !machineData || allMachineTypes.length === 0 ? (
            <EmptyState message="No active or pending deployments with machine data for this month" />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="lc-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    {allMachineTypes.map(mt => <th key={mt} style={{ textAlign: 'center' }}>{mt}</th>)}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {machineData.days.map(day => (
                    <tr key={day.date}>
                      <td style={{ fontWeight: 500 }}>{day.day} {MONTH_NAMES[machineData.month - 1].slice(0,3)}</td>
                      {allMachineTypes.map(mt => {
                        const m = day.machines.find(x => x.machine_type === mt);
                        const req = m?.required || 0;
                        const avail = m?.available ?? '—';
                        const deficient = m && m.deficiency > 0;
                        return (
                          <td key={mt} style={{ textAlign: 'center', fontSize: 11.5, color: deficient ? 'var(--red)' : 'var(--text-primary)', fontWeight: deficient ? 600 : 400 }}>
                            {req}/{avail}
                          </td>
                        );
                      })}
                      <td>
                        {day.has_deficiency
                          ? <Badge type="red">Short</Badge>
                          : <Badge type="green">OK</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Training need tab */}
      {tab === 'training' && (
        <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
          <div style={{ padding: '10px 14px', fontSize: 11.5, color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}>
            Pending-activation deployments only — gaps between required skill% and best available skill% in the factory's employee pool.
          </div>
          {loadingTraining ? <Loading /> : !trainingData || trainingData.gaps.length === 0 ? (
            <EmptyState message="No training gaps found for pending deployments" />
          ) : (
            <table className="lc-table">
              <thead>
                <tr>
                  <th>Line</th><th>Style</th><th>Operation</th><th>SAM</th>
                  <th>Req skill%</th><th>Available skill%</th><th>Gap</th>
                </tr>
              </thead>
              <tbody>
                {trainingData.gaps.map((g, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{g.line_number}</td>
                    <td>{g.style_number}</td>
                    <td>{g.operation_name}</td>
                    <td>{g.sam}</td>
                    <td>{g.required_skill_pct}%</td>
                    <td style={{ color: 'var(--red)', fontWeight: 600 }}>{g.available_skill_pct}%</td>
                    <td style={{ color: 'var(--red)' }}>-{g.gap}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}